import * as React from "react";
import { cn } from "../../lib/cn";

export interface SurfaceToolbarProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional leading slot — usually filter chips. */
  leading?: React.ReactNode;
  /** Right-aligned actions — search, view-mode, primary CTA. */
  children?: React.ReactNode;
}

/**
 * SurfaceToolbar — secondary action bar that lives BELOW the
 * CompanionHero. Holds filter chips on the left and a search /
 * action cluster on the right.
 *
 * Pure layout: each surface decides what chips / inputs to render. We
 * keep filter chips as a tabbed-segmented strip (v18 `.filter-row`).
 */
export const SurfaceToolbar = React.forwardRef<HTMLDivElement, SurfaceToolbarProps>(
  ({ leading, children, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-row items-center justify-between gap-3 px-8 pb-3",
        className,
      )}
      {...rest}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {leading}
      </div>
      {children ? (
        <div className="flex shrink-0 items-center gap-2">{children}</div>
      ) : null}
    </div>
  ),
);
SurfaceToolbar.displayName = "SurfaceToolbar";

// ── Helper sub-components — DRY filter chips reused across surfaces ───────

export interface SurfaceFilterChipProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

export const SurfaceFilterChip = React.forwardRef<
  HTMLButtonElement,
  SurfaceFilterChipProps
>(({ active, className, children, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-pressed={active}
    className={cn(
      "rounded-full px-3 py-1 font-medium text-[11.5px] leading-none transition-all",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-1",
      active
        ? "bg-gradient-to-b from-ink-2 to-[#3A3835] text-white shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.08)]"
        : "bg-transparent text-ink-2 hover:bg-white/60 hover:text-ink-1",
      className,
    )}
    {...rest}
  >
    {children}
  </button>
));
SurfaceFilterChip.displayName = "SurfaceFilterChip";
